import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppThunk } from '../store'
import UsersPage from 'model/UsersPage'
import UsersPagesRepository from 'clients/db/UsersPagesRepository'
import User from 'model/User'
import UserRepository from 'clients/db/UserRepository'

interface UsersState {
  isLoading: boolean
  usersPages: UsersPage[]
  user: User
}

const initialState: UsersState = {
  isLoading: false,
  usersPages: [],
  user: {} as User,
}

function startLoading(state: UsersState) {
  state.isLoading = true
}

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    fetchUsersStart: startLoading,
    updateUserStart: startLoading,
    fetchUsersFirstPageSuccess: (state, { payload }: PayloadAction<UsersPage>) => {
      state.isLoading = false
      state.usersPages = [payload]
    },
    fetchUsersPageSuccess: (state, { payload }: PayloadAction<UsersPage>) => {
      state.isLoading = false
      state.usersPages = [...state.usersPages, payload]
    },
    fetchUserSuccess: (state, { payload }: PayloadAction<User>) => {
      state.isLoading = false
      state.user = payload
    },
    updateUserSuccess(state, { payload }: PayloadAction<User>) {
      state.isLoading = false
      state.user = payload
    },
    updateUserFailed(state: UsersState) {
      state.isLoading = false
    },
    removeUser(state: UsersState) {
      state.user = {} as User
    },
  },
})
export const {
  fetchUsersStart,
  fetchUsersFirstPageSuccess,
  fetchUsersPageSuccess,
  fetchUserSuccess,
  updateUserStart,
  updateUserSuccess,
  updateUserFailed,
  removeUser,
} = usersSlice.actions

export const fetchUsersPage = (bookmark: string, searchString?: string): AppThunk => async (
  dispatch,
) => {
  dispatch(fetchUsersStart())

  let usersPages
  if (searchString === undefined || searchString.trim() === '') {
    usersPages = await UsersPagesRepository.searchUsersPage(bookmark)
  } else {
    usersPages = await UsersPagesRepository.searchUsersPageText(bookmark, searchString)
  }

  if (bookmark === '') {
    dispatch(fetchUsersFirstPageSuccess(usersPages))
  } else {
    dispatch(fetchUsersPageSuccess(usersPages))
  }
}

export const fetchUser = (id: string): AppThunk => async (dispatch) => {
  dispatch(fetchUsersStart())
  const user = await UserRepository.find(id)
  dispatch(fetchUserSuccess(user))
}

export const createUser = (
  user: User,
  onSuccess?: (user: User) => void,
  onError?: (error: any) => void,
): AppThunk => async (dispatch) => {
  dispatch(updateUserStart())

  try {
    const createdUser = await UserRepository.saveOrUpdate(user)
    console.log('createdUser', createdUser)

    dispatch(updateUserSuccess(createdUser))
    if (onSuccess) {
      onSuccess(createdUser)
    }
  } catch (error) {
    dispatch(updateUserFailed())

    if (onError) {
      onError(error)
    }
  }
}

export const updateUser = (
  user: User,
  onSuccess?: (user: User) => void,
  onError?: (error: any) => void,
): AppThunk => async (dispatch) => {
  dispatch(updateUserStart())
  console.log('updateUserStart')

  try {
    const updatedUser = await UserRepository.saveOrUpdate(user)
    console.log('updatedPatient', updatedUser)

    dispatch(updateUserSuccess(updatedUser))
    if (onSuccess) {
      onSuccess(updatedUser)
    }
  } catch (error) {
    dispatch(updateUserFailed())

    if (onError) {
      onError(error)
    }
  }
}

export const fetchUsersPageGroup = (
  groupId: string,
  bookmark: string,
  searchString?: string,
): AppThunk => async (dispatch) => {
  dispatch(fetchUsersStart())

  let usersPages
  if (searchString === undefined || searchString.trim() === '') {
    usersPages = await UsersPagesRepository.searchUsersPageInGroup(groupId, bookmark)
  } else {
    usersPages = await UsersPagesRepository.searchUsersPageInGroupText(
      groupId,
      bookmark,
      searchString,
    )
  }

  if (bookmark === '') {
    dispatch(fetchUsersFirstPageSuccess(usersPages))
  } else {
    dispatch(fetchUsersPageSuccess(usersPages))
  }
}

export const fetchUsersPageMainGroup = (
  bookmark: string,
  searchString?: string,
): AppThunk => async (dispatch) => {
  dispatch(fetchUsersStart())

  let usersPages
  if (searchString === undefined || searchString.trim() === '') {
    usersPages = await UsersPagesRepository.searchUsersPageInMainGroup(bookmark)
  } else {
    usersPages = await UsersPagesRepository.searchUsersPagePageInMainGroupText(
      bookmark,
      searchString,
    )
  }

  if (bookmark === '') {
    dispatch(fetchUsersFirstPageSuccess(usersPages))
  } else {
    dispatch(fetchUsersPageSuccess(usersPages))
  }
}

export const fetchUsersPageUngrouped = (
  bookmark: string,
  searchString?: string,
): AppThunk => async (dispatch) => {
  dispatch(fetchUsersStart())

  let usersPages
  if (searchString === undefined || searchString.trim() === '') {
    usersPages = await UsersPagesRepository.searchUsersPageInSystem(bookmark)
  } else {
    usersPages = await UsersPagesRepository.searchUsersPagePageInSystemText(bookmark, searchString)
  }

  if (bookmark === '') {
    dispatch(fetchUsersFirstPageSuccess(usersPages))
  } else {
    dispatch(fetchUsersPageSuccess(usersPages))
  }
}

export const fetchUsersPageSystem = (bookmark: string, searchString?: string): AppThunk => async (
  dispatch,
) => {
  dispatch(fetchUsersStart())

  let usersPages
  if (searchString === undefined || searchString.trim() === '') {
    usersPages = await UsersPagesRepository.searchUsersPageInSystem(bookmark)
  } else {
    usersPages = await UsersPagesRepository.searchUsersPagePageInSystemText(bookmark, searchString)
  }

  if (bookmark === '') {
    dispatch(fetchUsersFirstPageSuccess(usersPages))
  } else {
    dispatch(fetchUsersPageSuccess(usersPages))
  }
}

export default usersSlice.reducer
